import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { v4 as uuid } from 'uuid'

import * as templateInstallationActions from 'apps/templates/actions/templateInstallation'
import * as templatesActions from 'apps/templates/actions/templatesActions'
import steps from 'apps/templates/constants/TemplateInstallationSteps'
import * as helpers from 'apps/templates/models/Template/helpers'
import * as installedTemplateSelectors from 'apps/templates/selectors/installedTemplateSelectors'
import * as templateInstallationSelectors from 'apps/templates/selectors/templateInstallationSelectors'
import { TemplateAnalytics } from 'apps/templates/utils/TemplateAnalytics'
import * as templateStoreActions from 'apps/templateStore/actions/templateStoreActions'
import * as appSelectors from 'common/core/selectors/appSelectors'

import InstallationWizardLayout from './InstallationWizardLayout'

const InstallationWizardLayoutController = (props) => {
  const navigate = useNavigate()
  const pageId = useAppSelector(appSelectors.getCurrentAccountID)
  const template = useAppSelector(templateInstallationSelectors.getTemplateData)
  const error = useAppSelector(templateInstallationSelectors.getInstallationError)
  const isInstalling = useAppSelector(
    templateInstallationSelectors.isInstallingComplete,
  ).isInstalling
  const isInstallingComplete = useAppSelector(
    templateInstallationSelectors.isInstallingComplete,
  ).installingComplete

  const installationIdFromStore = useAppSelector(templateInstallationSelectors.getInstallationId)
  const installationId = props.installationId || installationIdFromStore
  const isConfigurationPage = props.isConfigurationPage
  const installedTemplate = useAppSelector(installedTemplateSelectors.getCurrent)
  const fetchingInstalledTemplate = useAppSelector(installedTemplateSelectors.isFetching)
  const { template_hash: templateHash, template_id: templateId } = useParams()
  const hasUnmappedGAFs = useAppSelector(templateInstallationSelectors.getUnmappedGAFs).length

  const dispatch = useAppDispatch()

  const openInstalledTemplate = React.useMemo(
    () => () => {
      helpers.openInstalledTemplatePage(pageId, installationId)
    },
    [installationId, pageId],
  )

  const handleChangeStep = React.useMemo(
    () => (step) => {
      let url = `/template/${templateHash || templateId}/`

      if (step === steps.STEP1) {
        url += steps.STEP1
      }
      if (step === steps.STEP2) {
        url += steps.STEP2
      }
      if (step === steps.STEP4) {
        url += `install/${pageId}/${steps.STEP4}/${installationId}`
      }
      navigate(url)
    },
    [templateHash, templateId, navigate, pageId, installationId],
  )

  React.useEffect(() => {
    dispatch(
      templateHash
        ? templateInstallationActions.fetchBotTemplate(templateHash)
        : templateStoreActions.fetchTemplate(templateId),
    )
  }, [dispatch, templateHash, templateId])

  React.useEffect(() => {
    if (
      isInstallingComplete &&
      pageId &&
      installationId &&
      !installedTemplate &&
      !fetchingInstalledTemplate
    ) {
      TemplateAnalytics.pageEvents.logFinalizePageInstalled({
        templateHash,
        childTemplateId: installationId,
      })
      try {
        dispatch(templatesActions.fetchInstalledTemplate(installationId))
      } catch (err) {
        openInstalledTemplate()
      }
    }
  }, [
    isInstallingComplete,
    pageId,
    installationId,
    installedTemplate,
    fetchingInstalledTemplate,
    dispatch,
    openInstalledTemplate,
    templateHash,
  ])

  React.useEffect(() => {
    if (!installedTemplate) {
      return
    }

    if (installedTemplate.showWizard && hasUnmappedGAFs) {
      return handleChangeStep(steps.STEP4)
    }

    if (props.step !== steps.STEP4) {
      openInstalledTemplate()
    }
  }, [installedTemplate, hasUnmappedGAFs, openInstalledTemplate, handleChangeStep, props.step])

  const handelInstall = () =>
    dispatch(
      templateInstallationActions.installBotTemplate(templateHash || templateId, pageId, uuid()),
    )

  return (
    <InstallationWizardLayout
      step={props.step}
      template={template}
      error={error}
      isInstalling={isInstalling}
      isInstallingComplete={isInstallingComplete}
      onChangeStep={handleChangeStep}
      onSkipWizard={openInstalledTemplate}
      onInstallTemplate={handelInstall}
      installationId={installationId}
      isConfigurationPage={isConfigurationPage}
    >
      {props.children}
    </InstallationWizardLayout>
  )
}

export default InstallationWizardLayoutController
